import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { setSelectedCategory, fetchMenuDataRequest, MenuItem } from '../redux/slices/menuSlice';
import { addToCart, openCart, clearCart } from '../redux/slices/cartSlice';
import { fetchComboRequest } from '../redux/slices/comboSlice';
import { replaceMenuWidgetType, updateSiteContentRequest } from '../redux/slices/siteContentSlice';
import { createCartItem, createBuilderCartItem } from '../utils/cartUtils';
import { shouldShowSpiceLevel } from '../utils/spiceUtils';
import { filterEnabledModifiers, isModifierRequired } from '../utils/modifierUtils';
import { parsePrice } from '../utils/priceUtils';
import { FiLayout } from 'react-icons/fi';
import {
  StatusMessage,
  MenuGrid,
  CategoryFilter,
  MenuSearch,
  BackButton
} from '../components';
import MenuStyleSelectionModal from '../components/MenuStyleSelectionModal';
import { MenuItemCardVariant } from '../components/MenuGrid';
import { AddToCartModal } from '../components/AddToCartModal';
import { ModifierBuilderModal } from '../components/ModifierBuilderModal';
import { ModifierBuilder } from '../components/ModifierBuilder';
import { PaymentSuccessPopup, PaymentFailedProcessingPopup } from '../components/checkout';
import DietaryFilters, { DietaryFilter, useDietaryFilterLogic, applyDietaryFilters } from '../components/DietaryFilters';
import MenuToggle from '../components/MenuToggle';
import ComboCard from '../components/ComboCard';
import BuilderCategoryFilter from '../components/BuilderCategoryFilter';
import { fetchTpnConfigRequest } from '../redux/slices/tpnConfigSlice';
import { useTpnCart } from '../hooks/useTpnCart';

const MenuPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const appNavigate = useAppNavigate();
  const { loading, error, data: menuData, selectedCategory } = useAppSelector(state => state.menu);
  const { loading: comboLoading, data: comboData } = useAppSelector(state => state.combo);
  const { data: siteContentData, updateLoading } = useAppSelector(state => state.siteContent);
  const { isEditControl } = useAppSelector(state => state.editControl);
  const shouldShowCart = useTpnCart(true);

  // Track if we're saving menu style to close modal after success
  const isSavingMenuStyle = useRef(false);
  const prevUpdateLoading = useRef<boolean>(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [modalItem, setModalItem] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModifierBuilderOpen, setIsModifierBuilderOpen] = useState(false);
  const [activeDietaryFilters, setActiveDietaryFilters] = useState<Set<DietaryFilter>>(new Set());
  const [isComboMode, setIsComboMode] = useState(false);
  const [showPaymentFailedPopup, setShowPaymentFailedPopup] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [selectedBuilderItemId, setSelectedBuilderItemId] = useState<string | null>(null);
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const { handleDietaryFilterToggle } = useDietaryFilterLogic();

  // Handle menu style selection - save and close after API success
  const handleSelectWidget = useCallback((widgetType: string) => {
    // Mark that we're saving to close modal after API completes
    isSavingMenuStyle.current = true;
    // Update Redux
    dispatch(replaceMenuWidgetType(widgetType));
    // Save to API
    dispatch(updateSiteContentRequest());
    // DON'T close modal here - let useEffect close it after API success
  }, [dispatch]);

  // Close modal after API successfully saves menu style
  useEffect(() => {
    // Only close when loading transitions from true → false
    if (isSavingMenuStyle.current && prevUpdateLoading.current && !updateLoading) {
      setShowWidgetModal(false);
      isSavingMenuStyle.current = false;
    }
    // Track previous loading state
    prevUpdateLoading.current = updateLoading;
  }, [updateLoading]);

  // Dynamic cardVariant based on Redux menuWidgetTypes
  const cardVariant = useMemo((): MenuItemCardVariant => {
    if (!siteContentData?.menuWidgetTypes || siteContentData.menuWidgetTypes.length === 0) {
      return 'modern'; // default
    }

    const menuType = siteContentData.menuWidgetTypes[0]; // Get first menu widget type

    // Map widget types to card variants
    if (menuType.includes('Basic')) return 'basic';
    if (menuType.includes('Classic')) return 'classic';
    if (menuType.includes('Prime')) return 'prime';
    return 'modern'; // default for Modern or unknown types
  }, [siteContentData?.menuWidgetTypes]);

  useEffect(() => {
    console.log('🔍 MenuPage loaded with URL:', window.location.href);
    console.log('🔍 URL search params:', window.location.search);
    
    const searchParams = new URLSearchParams(window.location.search);
    const responseCode = searchParams.get('responseCode');
    const transactionId = searchParams.get('transactionId');
    const transactionReferenceId = searchParams.get('transactionReferenceId');
    const paymentStatus = searchParams.get('payment_status');

    console.log('🔍 Payment parameters found:', {
      responseCode,
      transactionId,
      transactionReferenceId,
      paymentStatus,
      hasAnyPaymentParam: !!(responseCode || transactionId || transactionReferenceId || paymentStatus)
    });

    if (responseCode || transactionId || transactionReferenceId || paymentStatus) {
      console.log('💳 Payment callback detected in MenuPage');

      // Process payment callback
      if (responseCode === '200' || paymentStatus?.toLowerCase() === 'success') {
        console.log('✅ Payment success from callback');
        setShowPaymentSuccessPopup(true);
      } else if (responseCode && responseCode === '400') {
        console.log('❌ Payment failed from callback:', responseCode);
        setShowPaymentFailedPopup(true);
      } else if (transactionId || transactionReferenceId) {
        console.log('✅ Payment callback with transaction ID, assuming success');
        setShowPaymentSuccessPopup(true);
      }

      // DO NOT clean URL - let's see what clears it
      console.log('🔍 Payment processed, keeping URL intact to see what clears it');
    } else {
      console.log('🔍 No payment parameters found in URL');
    }
  }, []);
  // Fetch menu data and combo data on component mount if not already loaded
  useEffect(() => {
    dispatch(fetchMenuDataRequest());
    dispatch(fetchTpnConfigRequest())
    dispatch(fetchComboRequest());
  }, [dispatch]);

  const handleCategoryChange = useCallback((category: string | null) => {
    dispatch(setSelectedCategory(category));
  }, [dispatch]);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleItemClick = useCallback((item: any) => {
    appNavigate(`/menu-item/${item.pk_id}`);
  }, [appNavigate]);

  // Check if item needs customization before adding to cart
  const needsCustomization = useCallback((item: MenuItem): boolean => {
    const hasSpiceLevel = shouldShowSpiceLevel(item);
    const enabledModifiers = filterEnabledModifiers(item.modifiers_list || []);
    console.log('enabledModifiers',enabledModifiers)
    const hasRequiredModifiers = enabledModifiers.some(modifier => isModifierRequired(modifier));
    return hasSpiceLevel || hasRequiredModifiers;
  }, []);

  const handleAddToCart = useCallback((item: MenuItem) => {
    // Check if item has builder_type set to 'salad' (case-insensitive)
    const isSaladBarItem = item.builder_type?.toLowerCase() === 'salad';
    
    if (isSaladBarItem) {
      // Open ModifierBuilderModal for builder items
      setModalItem(item);
      setIsModifierBuilderOpen(true);
      return;
    }

    if (needsCustomization(item)) {
      // Open modal for customization
      setModalItem(item);
      setIsModalOpen(true);
    } else {
      // Add directly to cart for simple items
      const cartItem = {
        item,
        selected: {
          spice: null,
          modifiers: [],
        },
        totalPrice: parsePrice(item.price),
        quantity: 1,
        cartItemId: `${item.pk_id}-basic-${Date.now()}`,
      };
      dispatch(addToCart(cartItem));
      // Automatically open cart after adding item
      dispatch(openCart());
    }
  }, [dispatch, needsCustomization]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalItem(null);
  }, []);

  const handleModifierBuilderClose = useCallback(() => {
    setIsModifierBuilderOpen(false);
  }, []);

  const handleModifierBuilderAddToCart = useCallback((
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: any[]
  ) => {
    const cartItem = createBuilderCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    dispatch(openCart());
  }, [dispatch]);

  const handleModalAddToCart = useCallback((
    item: MenuItem,
    quantity: number,
    spiceLevel: number | null,
    modifiers: any[]
  ) => {
    const cartItem = createCartItem(item, spiceLevel, modifiers, quantity);
    dispatch(addToCart(cartItem));
    // Close modal and open cart
    setIsModalOpen(false);
    setModalItem(null);
    dispatch(openCart());
  }, [dispatch]);

  // Handle dietary filter toggle using the logic from DietaryFilters component
  const onDietaryFilterToggle = useCallback((filter: DietaryFilter) => {
    handleDietaryFilterToggle(filter, activeDietaryFilters, setActiveDietaryFilters);
  }, [handleDietaryFilterToggle, activeDietaryFilters]);

  // Check if combo data exists and has enabled combos
  const hasComboData = useMemo(() => {
    console.log('Combo data check:', { comboData, isArray: Array.isArray(comboData), combosLength: comboData?.length });
    return comboData && Array.isArray(comboData) && comboData.length > 0;
  }, [comboData]);

  const enabledCombos = useMemo(() => {
    if (!comboData || !Array.isArray(comboData)) return [];
    return comboData.filter((combo: any) => combo.is_enabled === true);
  }, [comboData]);

  const totalComboCount = comboData?.length || 0;

  // Smart URL cleaning - only clean after both APIs load successfully
  const cleanUrlAfterPaymentProcessing = useCallback(() => {
    const bothApisLoaded = !loading && !comboLoading;
    
    if (bothApisLoaded) {
      // Clean URL parameters after successful API loading
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
      console.log('🧹 URL cleaned after payment processing and API success');
    } else {
      console.log('⏳ Waiting for APIs to load before cleaning URL - Menu loading:', loading, 'Combo loading:', comboLoading);
    }
  }, [loading, comboLoading]);

  // Handle combo add to cart
  const handleComboAddToCart = useCallback((combo: any) => {
    const comboCartItem = {
      item: {
        pk_id: `combo-${combo.combo_id}`,
        name: combo.title,
        description: combo.description,
        price: combo.combo_price.replace('$', ''),
        image: combo.combo_image,
        is_combo: true,
        combo_data: combo,
      },
      selected: {
        spice: null,
        modifiers: [],
      },
      totalPrice: parsePrice(combo.combo_price),
      cartItemId: `combo-${combo.combo_id}-${Date.now()}`,
      quantity: 1,
    };
    console.log()
    dispatch(addToCart(comboCartItem));
    dispatch(openCart());
  }, [dispatch]);

  // Filter combos based on search and category
  const filteredCombos = useMemo(() => {
    let filtered = enabledCombos;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((combo: any) =>
        combo.title.toLowerCase().includes(query) ||
        combo.description.toLowerCase().includes(query)
      );
    }

    // Apply default sorting by title
    return [...filtered].sort((a: any, b: any) => a.title.localeCompare(b.title));
  }, [enabledCombos, searchQuery]);

  // Pre-filter menu items to match CategoryFilter logic
  const availableMenuItems = useMemo(() => {
    if (!menuData) return [];
    return menuData.filter(item => 
      item.is_enabled && 
      item.is_item_verified
    );
  }, [menuData]);

  // Filter and sort menu items
  const filteredAndSortedMenuItems = useMemo(() => {
    if (!availableMenuItems) return [];

    let filtered = availableMenuItems;

    // Apply dietary filters first
    if (activeDietaryFilters.size > 0) {
      filtered = applyDietaryFilters(availableMenuItems, comboData || [], activeDietaryFilters);
    }

    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(item =>
        item.category === selectedCategory ||
        item.level1_category === selectedCategory ||
        item.level2_category === selectedCategory
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)
      );
    }

    // Apply default sorting by name
    const sorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name));

    return sorted;
  }, [availableMenuItems, selectedCategory, searchQuery, activeDietaryFilters, comboData]);

  // Separate salad items from regular items
  const { saladItems, regularItems } = useMemo(() => {
    const salads: MenuItem[] = [];
    const regular: MenuItem[] = [];
    
    filteredAndSortedMenuItems.forEach(item => {
      if (item.builder_type?.toLowerCase() === 'salad') {
        salads.push(item);
      } else {
        regular.push(item);
      }
    });
    
    return { saladItems: salads, regularItems: regular };
  }, [filteredAndSortedMenuItems]);

  // Auto-select first builder item when salad items load (but NOT category)
  useEffect(() => {
    if (saladItems.length > 0) {
      const firstSaladItem = saladItems[0];
      
      // Check if currently selected item exists in filtered list
      const isCurrentItemInList = saladItems.some(
        item => item.pk_id === selectedBuilderItemId
      );
      
      // 3. Auto-select first builder item if:
      //    - No item selected, OR
      //    - Selected item not in current category (category changed)
      if (!selectedBuilderItemId || !isCurrentItemInList) {
        setSelectedBuilderItemId(firstSaladItem.pk_id);
      }
    }
  }, [saladItems, selectedBuilderItemId, dispatch]);

  // Filter salad items by selected builder item ID
  const filteredSaladItems = useMemo(() => {
    if (!selectedBuilderItemId) {
      return []; // No item selected = no items shown (accordion collapsed)
    }
    
    return saladItems.filter(item => item.pk_id === selectedBuilderItemId);
  }, [saladItems, selectedBuilderItemId]);

  const handleBuilderItemChange = useCallback((itemId: string | null) => {
    setSelectedBuilderItemId(itemId);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 md:pt-36 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg shadow-md p-6">
                  <div className="h-48 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return <StatusMessage message={`Error Loading Menu: ${error}`} type="error" />;
  }

  if (!menuData || menuData.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 md:pt-36 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🍽️</div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Menu Available</h2>
            <p className="text-gray-600">
              Menu items are not available at the moment. Please check back later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-white pt-20 md:pt-36 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Back Button */}
          <div className="mb-6">
            <BackButton text="Back to Home" onClick={() => appNavigate('/')} />
            
            {/* Title - Always at top on mobile, desktop: same row with search */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 md:gap-6">
              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold">Our Menu</h1>
              
              {/* Search and Filters - Hidden on mobile, shown on desktop in header */}
              <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
                {/* Search Bar */}
                <div className="w-full max-w-md">
                  <MenuSearch
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                  />
                </div>

                {/* Dietary Filters */}
                {!isComboMode && (
                  <DietaryFilters
                    activeDietaryFilters={activeDietaryFilters}
                    onDietaryFilterToggle={onDietaryFilterToggle}
                    hasEnabledCombos={false}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Mobile Search and Filters - Above Category Filter */}
          {!isComboMode && (
            <div className="md:hidden space-y-4">
              {/* Dietary Filters */}
              <DietaryFilters
                activeDietaryFilters={activeDietaryFilters}
                onDietaryFilterToggle={onDietaryFilterToggle}
                hasEnabledCombos={false}
              />

              {/* Search Bar */}
              <MenuSearch
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
              />
            </div>
          )}

          {/* Category Filter - Sticky Header - Only show for individual items */}
          {!isComboMode && (
            <div className="sticky top-16 md:top-20 lg:top-20 z-10 -mx-4 px-4 mb-8 bg-white">
              <CategoryFilter
                menuItems={menuData}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
              />
            </div>
          )}

          {/* Menu Toggle - Show if combo data exists */}
          {/* {hasComboData && (
            <MenuToggle
              isComboMode={isComboMode}
              onToggle={setIsComboMode}
              comboCount={totalComboCount}
            />
          )} */}



          {/* Results Count */}
          {/* <div className="mb-4">
            <p className="text-sm text-gray-600">
              {isComboMode
                ? `Showing ${filteredCombos.length} of ${enabledCombos.length} combos`
                : `Showing ${filteredAndSortedMenuItems.length} of ${menuData.length} items`
              }
            </p>
          </div> */}

          {/* Content Display */}
          <div>
            {isComboMode ? (
              // Combo Grid
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCombos.length > 0 ? (
                  filteredCombos.map((combo: any) => (
                    <ComboCard
                      key={combo.combo_id}
                      combo={combo}
                      variant="basic"
                      onAddToCart={shouldShowCart ? handleComboAddToCart : undefined}
                    />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <div className="text-6xl mb-4">🍽️</div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Combos Available</h2>
                    <p className="text-gray-600">
                      {searchQuery ? 'No combos match your search.' : 'No combo offers are currently available.'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Salad Bar Items - With Category Filter */}
                {saladItems.length > 0 && (
                  <div className="mb-8">
                    {/* Builder Item Filter */}
                    <BuilderCategoryFilter
                      builderItems={saladItems}
                      selectedItemId={selectedBuilderItemId}
                      onItemChange={handleBuilderItemChange}
                    />
                    
                    {/* Filtered Salad Items - Only shown when category is selected */}
                    {filteredSaladItems.length > 0 && (
                      <div className="mt-6">
                        {filteredSaladItems.map(item => (
                          <ModifierBuilder
                            key={item.pk_id}
                            item={item}
                            onAddToCart={handleModifierBuilderAddToCart}
                            autoExpand={true}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Regular Menu Items Grid */}
                {regularItems.length > 0 && (
                  <MenuGrid
                    menuItems={regularItems}
                    selectedCategory={null}
                    searchQuery=""
                    variant={cardVariant}
                    onItemClick={handleItemClick}
                    onAddToCart={shouldShowCart ? handleAddToCart : undefined}
                  />
                )}

                {/* Select Menu Card Style Button - Floating - Only show when edit control is enabled */}
                {isEditControl && (
                  <button
                    onClick={() => setShowWidgetModal(true)}
                    className="fixed bottom-6 right-6 px-4 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2 z-40"
                    style={{ backgroundColor: '#EE4446' }}
                    aria-label="Select menu card style"
                  >
                    <FiLayout className="w-5 h-5 text-white" />
                    <span className="text-white font-medium text-sm whitespace-nowrap">Select Menu Card Style</span>
                  </button>
                )}

                {/* Empty State */}
                {saladItems.length === 0 && regularItems.length === 0 && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🍽️</div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-2">No Items Found</h2>
                    <p className="text-gray-600">
                      {searchQuery ? 'No items match your search.' : 'No menu items are currently available.'}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add to Cart Modal */}
      <AddToCartModal
        isOpen={isModalOpen}
        item={modalItem}
        onClose={handleModalClose}
        onAddToCart={handleModalAddToCart}
      />

      {/* Modifier Builder Modal - Uses Dynamic Item Data */}
      <ModifierBuilderModal
        isOpen={isModifierBuilderOpen}
        item={modalItem || {} as MenuItem}
        onClose={handleModifierBuilderClose}
        onAddToCart={handleModifierBuilderAddToCart}
      />
      <PaymentFailedProcessingPopup
        isOpen={showPaymentFailedPopup}
        onClose={() => {
          setShowPaymentFailedPopup(false);
          cleanUrlAfterPaymentProcessing();
        }}
        onTryAgain={() => {
          setShowPaymentFailedPopup(false);
          cleanUrlAfterPaymentProcessing();
        }}
      />
      <PaymentSuccessPopup
        isOpen={showPaymentSuccessPopup}
        onClose={() => {
          setShowPaymentSuccessPopup(false);
          cleanUrlAfterPaymentProcessing();
        }}
        onContinue={() => {
          setShowPaymentSuccessPopup(false);
          dispatch(clearCart());
          cleanUrlAfterPaymentProcessing();
          // Stay on menu page after success
        }}
      />

      {/* Menu Style Selection Modal */}
      <MenuStyleSelectionModal
        isOpen={showWidgetModal}
        onClose={() => setShowWidgetModal(false)}
        onSelectStyle={handleSelectWidget}
        isLoading={updateLoading}
      />
    </>
  );
};

export default MenuPage;
